import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GET_DEVICE_LOGS } from '../../graphql/queries';
import {
  CheckCircle2,
  XCircle,
  Shield,
  UserCog,
  Loader2,
} from 'lucide-react';

interface DeviceLogDialogProps {
  device: { id: string; deviceName: string } | null;
  open: boolean;
  onClose: () => void;
}

type FilterType = 'ALL' | 'ERRORS' | 'ADMIN';

const EVENT_LABELS: Record<string, string> = {
  VERIFICATION_SUCCESS: 'Weryfikacja OK',
  VERIFICATION_FAILURE: 'Weryfikacja nieudana',
  AUTHENTICATION_SUCCESS: 'Uwierzytelnienie OK',
  AUTHENTICATION_FAILURE: 'Uwierzytelnienie nieudane',
  REGISTRATION: 'Rejestracja',
  REGISTRATION_WITH_TOKEN: 'Rejestracja tokenem',
  ADMIN_APPROVE: 'Zatwierdzenie',
  ADMIN_REJECT: 'Odrzucenie',
  ADMIN_BLOCK: 'Zablokowanie',
  ADMIN_DELETE: 'Usunięcie',
  ADMIN_UPDATE: 'Aktualizacja',
};

const ERROR_EVENTS = [
  'VERIFICATION_FAILURE',
  'AUTHENTICATION_FAILURE',
];

const ADMIN_EVENTS = [
  'ADMIN_APPROVE',
  'ADMIN_REJECT',
  'ADMIN_BLOCK',
  'ADMIN_DELETE',
  'ADMIN_UPDATE',
];

function getEventFilterValues(filter: FilterType): string[] | undefined {
  if (filter === 'ERRORS') return ERROR_EVENTS;
  if (filter === 'ADMIN') return ADMIN_EVENTS;
  return undefined;
}

function getEventBadge(event: string, success: boolean) {
  if (ADMIN_EVENTS.includes(event)) {
    return (
      <Badge className="bg-blue-100 text-blue-800 text-xs">
        <UserCog className="w-3 h-3 mr-1" />
        {EVENT_LABELS[event] || event}
      </Badge>
    );
  }
  if (success) {
    return (
      <Badge className="bg-green-100 text-green-800 text-xs">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {EVENT_LABELS[event] || event}
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-800 text-xs">
      <XCircle className="w-3 h-3 mr-1" />
      {EVENT_LABELS[event] || event}
    </Badge>
  );
}

const PAGE_SIZE = 50;

export const DeviceLogDialog: React.FC<DeviceLogDialogProps> = ({
  device,
  open,
  onClose,
}) => {
  const [filter, setFilter] = useState<FilterType>('ALL');
  const [offset, setOffset] = useState(0);

  const eventFilter = getEventFilterValues(filter);

  const { data, loading, fetchMore } = useQuery(GET_DEVICE_LOGS, {
    variables: {
      deviceId: device?.id || '',
      limit: PAGE_SIZE,
      offset: 0,
      eventFilter: eventFilter || null,
    },
    skip: !device?.id || !open,
    fetchPolicy: 'network-only',
  });

  const logs = data?.deviceLogs?.logs || [];
  const total = data?.deviceLogs?.total || 0;

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setOffset(0);
  };

  const handleLoadMore = () => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    fetchMore({
      variables: { offset: newOffset },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          deviceLogs: {
            ...fetchMoreResult.deviceLogs,
            logs: [...prev.deviceLogs.logs, ...fetchMoreResult.deviceLogs.logs],
          },
        };
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Logi urządzenia: {device?.deviceName}
          </DialogTitle>
          <DialogDescription>
            Historia zdarzeń i weryfikacji urządzenia
          </DialogDescription>
        </DialogHeader>

        {/* Filter buttons */}
        <div className="flex gap-2">
          {([
            ['ALL', 'Wszystkie'],
            ['ERRORS', 'Tylko błędy'],
            ['ADMIN', 'Akcje admina'],
          ] as [FilterType, string][]).map(([key, label]) => (
            <Button
              key={key}
              size="sm"
              variant={filter === key ? 'default' : 'outline'}
              onClick={() => handleFilterChange(key)}
            >
              {label}
            </Button>
          ))}
        </div>

        {/* Log list */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Brak logów dla wybranego filtra
            </div>
          ) : (
            <>
              {logs.map((log: any) => (
                <div
                  key={log.id}
                  className="border rounded-md p-3 text-sm space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getEventBadge(log.event, log.success)}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString('pl-PL')}
                    </span>
                  </div>
                  {log.reason && (
                    <p className="text-xs text-muted-foreground">
                      {log.reason}
                    </p>
                  )}
                  {log.ipAddress && (
                    <p className="text-xs text-muted-foreground">
                      IP: {log.ipAddress}
                    </p>
                  )}
                  {log.performedBy && (
                    <p className="text-xs text-muted-foreground">
                      Wykonał: {log.performedBy}
                    </p>
                  )}
                </div>
              ))}

              {logs.length < total && (
                <div className="flex justify-center pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : null}
                    Załaduj więcej ({logs.length}/{total})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
